'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { OtpVerifyForm } from '@/components/auth/otp-verify-form';
import { authClient } from '@/lib/auth-client';
import { authToasts } from '@/lib/utils/toast';
import { OtpRequestForm } from './otp-request-form';
import { PasswordLoginForm } from './password-login-form';

export function LoginForm() {
    const router = useRouter();
    const [loginMethod, setLoginMethod] = useState<'password' | 'otp' | 'sso'>(
        'password'
    );
    const [step, setStep] = useState<'enter_email' | 'enter_otp'>(
        'enter_email'
    );
    const [email, setEmail] = useState('');

    useEffect(() => {
        if (
            typeof window !== 'undefined' &&
            window.PublicKeyCredential &&
            window.PublicKeyCredential.isConditionalMediationAvailable
        ) {
            window.PublicKeyCredential.isConditionalMediationAvailable().then(
                (available) => {
                    if (available) {
                        authClient.signIn.passkey({ autoFill: true });
                    }
                }
            );
        }
    }, []);

    const handleOtpSent = (sentEmail: string) => {
        setEmail(sentEmail);
        setStep('enter_otp');
    };

    const handleUseDifferentEmail = () => {
        setStep('enter_email');
        setEmail('');
    };

    const handleSwitchToPassword = () => {
        setLoginMethod('password');
        setStep('enter_email');
        setEmail('');
    };

    const handleSwitchToOtp = () => {
        setLoginMethod('otp');
        setStep('enter_email');
        setEmail('');
    };

    if (loginMethod === 'password') {
        return <PasswordLoginForm onSwitchToOtp={handleSwitchToOtp} />;
    }

    if (step === 'enter_otp') {
        const handleVerify = async (otp: string) => {
            const response = await authClient.signIn.emailOtp({
                email,
                otp,
            });

            if (response.error) {
                authToasts.loginFailed(
                    response.error.message || 'Login failed'
                );
            } else {
                authToasts.loginSuccess();
                router.push('/account/settings');
            }
        };

        const handleResend = async () => {
            const response = await authClient.emailOtp.sendVerificationOtp({
                email,
                type: 'sign-in',
            });

            if (response.error) {
                authToasts.otpFailed(
                    response.error.message || 'Failed to send OTP'
                );
            } else {
                authToasts.otpSent();
            }

            return response;
        };

        return (
            <OtpVerifyForm
                email={email}
                onResend={handleResend}
                onSwitchToPassword={handleSwitchToPassword}
                onUseDifferentEmail={handleUseDifferentEmail}
                onVerify={handleVerify}
                showLoginOptions
                submitButtonText="Log In"
            />
        );
    }

    return (
        <OtpRequestForm
            key={email} // Use email as key to reset form when email changes
            onOtpSent={handleOtpSent}
            onSwitchToPassword={handleSwitchToPassword}
        />
    );
}
